import _toConsumableArray from '@babel/runtime-corejs3/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime-corejs3/helpers/slicedToArray';
import _setTimeout from '@babel/runtime-corejs3/core-js-stable/set-timeout';
import _sliceInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/slice';
import _concatInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/concat';
import { useRef, useState, useEffect, useCallback } from 'react';
import classes from './48e9cbc90d14bd7bebd449e01b24594f1dfa3464d6c151a296242db195c390c2.cfe-internal.js';

/**
 * Sets up a live region in order to make screen reader announcements.
 * @param politeness - Sets the politeness level for `aria-live`.
 * @returns - A function used to send an announcement to the screen reader.
 */
function useScreenReaderAnnouncement(politeness) {
  var ariaLiveElementRef = useRef();
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    messageQueue = _useState2[0],
    setMessageQueue = _useState2[1]; // An array of messages where the first item in the array is the oldest message
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isAnnouncingMessage = _useState4[0],
    setIsAnnouncingMessage = _useState4[1];

  // Creates the live region element
  useEffect(function () {
    ariaLiveElementRef.current = document.createElement('span');
    ariaLiveElementRef.current.classList.add(classes.ariaLiveElement);
    document.body.appendChild(ariaLiveElementRef.current);
    return function () {
      if (ariaLiveElementRef.current !== undefined) document.body.removeChild(ariaLiveElementRef.current);
    };
  }, []);

  // Sets the politeness of `aria-live`
  useEffect(function () {
    var _ariaLiveElementRef$c;
    if (politeness !== undefined) (_ariaLiveElementRef$c = ariaLiveElementRef.current) === null || _ariaLiveElementRef$c === void 0 || _ariaLiveElementRef$c.setAttribute('aria-live', politeness);
  }, [politeness]);

  // Announces oldest message in the queue
  useEffect(function () {
    if (!isAnnouncingMessage && messageQueue.length > 0) {
      var _ariaLiveElementRef$c2;
      setIsAnnouncingMessage(true);
      var oldestMessage = messageQueue[0];
      var delay;
      if (((_ariaLiveElementRef$c2 = ariaLiveElementRef.current) === null || _ariaLiveElementRef$c2 === void 0 ? void 0 : _ariaLiveElementRef$c2.textContent) === oldestMessage) {
        /*
         * If the message to be announced is the same as the previous message, the message needs to be reset.
         * The screen reader won't read the change unless the text has changed.
         */
        ariaLiveElementRef.current.textContent = '';
        delay = 200; // Gives the screen reader time to detect the new message (this number was chosen arbitrarily)
      } else {
        // If the message to be announced is different from the previous message, there is no need for a delay
        delay = 0;
      }
      _setTimeout(function () {
        if (ariaLiveElementRef.current !== undefined) ariaLiveElementRef.current.textContent = oldestMessage;
        setMessageQueue(function (currentMessageQueue) {
          return _sliceInstanceProperty(currentMessageQueue).call(currentMessageQueue, 1);
        }); // Removes the oldest message (i.e. first item) from the message queue since it was just announced
        setIsAnnouncingMessage(false);
      }, delay);
    }
  }, [isAnnouncingMessage, messageQueue]);

  /**
   * Sends a message for the screen reader to read.
   * @param message - The message that the screen reader will read.
   */
  var announceToScreenReader = useCallback(function (message) {
    setMessageQueue(function (currentMessageQueue) {
      var _context;
      return _concatInstanceProperty(_context = []).call(_context, _toConsumableArray(currentMessageQueue), [message]);
    });
  }, []);
  return announceToScreenReader;
}

export { useScreenReaderAnnouncement as default };
